import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CrewMerchantUi } from 'corso-types';
import api from '~/api';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';

const useQueryKey = () => {
  const storeId = useStoreId();
  return ['webhooks', storeId] as const;
};

export const useWebhook = (webhookId: number) => {
  const storeId = useStoreId();
  return useQuery({
    queryKey: ['webhooks', storeId, webhookId],
    queryFn: () => api.store(storeId).settings.webhooks.get(`${webhookId}`),
    enabled: !!webhookId,
    throwOnError: false,
  });
};

const deliveriesQueryKey = (
  storeId: `${number}`,
  webhookId: number,
  request?: CrewMerchantUi.ConfigWebhookDeliverySearchReq,
) =>
  request
    ? (['webhooks', storeId, 'deliveries', webhookId, request] as const)
    : (['webhooks', storeId, 'deliveries', webhookId] as const);

export const useWebhookDeliverySearch = (
  webhookId: number,
  request: CrewMerchantUi.ConfigWebhookDeliverySearchReq,
) => {
  const storeId = useStoreId();

  const query = useQuery({
    queryKey: deliveriesQueryKey(storeId, webhookId, request),
    queryFn: () =>
      api
        .store(storeId)
        .settings.webhooks.deliveries.search(`${webhookId}`, request),
    enabled: !!webhookId,
    // prevents the table from flickering when filters or the page change
    placeholderData: (previous) => previous,
  });

  return {
    ...query,
    data: query.data?.data ?? [],
    pageInfo: query.data?.pageInfo ?? { nextCursor: null, prevCursor: null },
    statusCounts: query.data?.statusCounts,
  };
};

export const useWebhookDeliveryRetry = (webhookId: number) => {
  const storeId = useStoreId();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (deliveryId: number) =>
      api
        .store(storeId)
        .settings.webhooks.deliveries.retry(`${webhookId}`, `${deliveryId}`),
    onSuccess: (delivery) => {
      toast.show(
        delivery.status === 'Delivered'
          ? 'Delivery succeeded'
          : `Retry attempted — delivery is ${delivery.status}`,
      );
      return queryClient.invalidateQueries({
        queryKey: deliveriesQueryKey(storeId, webhookId),
      });
    },
    onError: () => {
      toast.show('Failed to retry delivery');
    },
  });
};

export const useWebhookConfigData = () => {
  const queryKey = useQueryKey();

  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [, storeId] }) =>
      api.store(storeId).settings.webhooks.list(),
  });
};

export const useWebhookCreate = () => {
  const storeId = useStoreId();
  const queryKey = useQueryKey();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: api.store(storeId).settings.webhooks.create,
    onSuccess: () => {
      toast.show('Webhook created successfully');
      return queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.show('Failed to create webhook');
    },
  });
};

export const useWebhookUpdate = () => {
  const storeId = useStoreId();
  const queryKey = useQueryKey();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({
      webhookId,
      body,
    }: {
      webhookId: number;
      body: CrewMerchantUi.ConfigWebhookUpdate;
    }) => api.store(storeId).settings.webhooks.update(`${webhookId}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: () => {
      toast.show('Failed to update webhook');
    },
  });
};

export const useWebhookDelete = () => {
  const storeId = useStoreId();
  const queryKey = useQueryKey();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (webhookId: number) =>
      api.store(storeId).settings.webhooks.delete(`${webhookId}`),
    onSuccess: () => {
      toast.show('Webhook deleted successfully');
      return queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.show('Failed to delete webhook');
    },
  });
};
